import { Fragment, useCallback, useEffect } from "react";
import Button from "@components/Button/Button";
import { REMOVE_FOCUS_CSS } from "@components/Button/utils/constants";
import { ActionType } from "@components/Button/utils/types";
import IconImg from "@components/Icon/IconImg";
import { IconTypeEnum } from "@components/Icon/utils/types";
import useSendPulseGaData from "@features/pulse/hooks/useSendPulseGaData";
import { SHARE_PANEL_LIST } from "@features/pulse/utils/constants";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { useWindowSize } from "@hooks/useWindowSize";
import { cn } from "@util/helpers";

type PulseShareToolPopoverProps = {
  /** Title of the article to be shared */
  articleTitle: string;
  /** URL of the article to be shared */
  shareUrl: string;
  /** Text to be displayed in the share button */
  text: string;
  /** Number of shares for the article */
  shareCount: number;
  /** Additional CSS class for styling */
  className?: string;
  /** Callback function to be called after sharing */
  callback?: () => void;
  /** Function to handle setting autoplay enabled state */
  handleSetIsAutoplayPaused: (isPaused: boolean) => void;
  /** Article ID for individual id for component */
  articleId?: string;
  /** Currently active article index */
  activeArticle?: number;
};

/** Pulse share tool
 * Popover for desktop
 * Button for mobile
 * Displays a share button that opens a popover with social media sharing options.
 * Displays share count if it is 10 or more.
 */
const PulseShareToolPopover = ({
  articleTitle,
  shareUrl,
  text,
  className,
  shareCount,
  callback,
  articleId,
  handleSetIsAutoplayPaused,
  activeArticle,
}: PulseShareToolPopoverProps): React.ReactElement => {
  const { isScreenLG } = useWindowSize();
  const { sendCustomEvent } = useSendPulseGaData();

  const handleCheckIfSharePopoverIsClose = useCallback(
    (mutation: MutationRecord[]) => {
      if (!mutation || mutation.length === 0) return;

      if (typeof handleSetIsAutoplayPaused !== "function") return;

      const isOpen = (mutation[0].target as Element).getAttribute(
        "data-headlessui-state"
      );

      handleSetIsAutoplayPaused(isOpen === "open");
    },
    [handleSetIsAutoplayPaused]
  );

  useEffect(() => {
    // Add mutation observer to detect changes if there a change in app width and set the dimension of the header
    if (typeof window === "undefined") return;

    const id = `#pulse-share-popover-component-${articleId}`;
    const appElement = window.document.querySelector(id);

    if (!appElement) return;

    const observer = new MutationObserver(handleCheckIfSharePopoverIsClose);

    observer.observe(appElement, {
      attributes: true,
      attributeFilter: ["data-headlessui-state"],
    });

    return () => {
      observer.disconnect();
    };
  }, [articleId, handleCheckIfSharePopoverIsClose]);

  const handleOnClickSendData = () => {
    if (typeof window === "undefined") return;

    sendCustomEvent(`mobile::click share::${activeArticle}`, articleTitle);
  };

  const shareOnClick = async () => {
    handleOnClickSendData();

    if (isScreenLG) return;

    if (typeof handleSetIsAutoplayPaused === "function") {
      handleSetIsAutoplayPaused(true);
    }

    /** Wait for user to complete the navigator action before enabled the autoplay */
    try {
      await navigator.share({
        text: articleTitle,
        url: shareUrl,
      });
    } finally {
      if (typeof handleSetIsAutoplayPaused === "function") {
        handleSetIsAutoplayPaused(false);
      }
    }

    if (typeof callback !== "function") return;
    callback();
  };

  if (!isScreenLG) {
    return (
      <Button
        actionType={ActionType.actionButton}
        className={cn("h-full", className)}
        onClick={shareOnClick}
        testId="article-social-sharing-component"
      >
        <IconImg
          icon={IconTypeEnum.shareLine}
          iconSize={24}
          imgAlt="share icon"
        />

        {text ? <p className="mb-0 ml-2 mr-[6px]">{text}</p> : null}

        {shareCount >= 10 ? (
          <p className="mb-0 text-gray-515">{shareCount}</p>
        ) : null}
      </Button>
    );
  }

  const sendSocialShareData = (social: string) => {
    if (!social) return;
    if (typeof window === "undefined") return;

    sendCustomEvent(
      `desktop::click share ${social}::${activeArticle}`,
      articleTitle
    );

    if (typeof callback !== "function") return;
    callback();
  };

  return (
    <Popover
      className={cn("relative block w-1/2")}
      data-testid="pulse-share-popover-component"
      id={`pulse-share-popover-component-${articleId}`}
    >
      <PopoverButton
        data-testid="pulse-share-popover-button"
        className={cn(
          REMOVE_FOCUS_CSS,
          "flex items-center justify-center",
          className
        )}
      >
        <IconImg
          icon={IconTypeEnum.shareLineWhite}
          iconSize={24}
          imgAlt="share icon"
        />

        {text ? <p className="mb-0 ml-2 mr-[6px]">{text}</p> : null}

        {shareCount >= 10 ? (
          <p className="mb-0 text-[#737373]">{shareCount}</p>
        ) : null}
      </PopoverButton>

      <Transition>
        <TransitionChild
          as={Fragment}
          enter="transition ease duration-500 transform"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition ease duration-150 transform"
          leaveFrom="opacity-100 "
        >
          <PopoverPanel
            data-testid="article-popover-panel"
            anchor={{ to: "bottom start", offset: 50, gap: -12 }}
            className={cn(
              "relative z-[100000] overflow-hidden rounded-md border border-white/10 bg-[#232323] py-3"
            )}
          >
            {SHARE_PANEL_LIST.map((item) => {
              return (
                <Button
                  key={item.text}
                  icon={item.icon}
                  whichSocialMedia={item.whichSocialMedia}
                  actionType={ActionType.shareButton}
                  className="m-0 w-full items-center justify-start gap-3 rounded-none border-none px-6 py-2 text-white hover:bg-[#363636]"
                  imgAlt={articleTitle}
                  title={articleTitle}
                  shareUrl={shareUrl}
                  text={item.text}
                  callback={() => {
                    sendSocialShareData(item.whichSocialMedia);
                  }}
                />
              );
            })}
          </PopoverPanel>
        </TransitionChild>
      </Transition>
    </Popover>
  );
};

export default PulseShareToolPopover;
